import { z } from 'zod';

// Tool values as a const array for reuse
export const TOOL_VALUES = [
  'capture_availability',
  'capture_consent',
  'capture_lead',
  'search_car',
  'request_human_callback',
  'transfer_to_human',
  'send_finance_link',
  'capture_finance_application',
  'end_call',
  'hold_for_transfer',
  'google_maps_search',
  'web_search',
  'show_progress',
] as const;

// Zod schema for tool enum
export const toolSchema = z.enum(TOOL_VALUES);
export type Tool = z.infer<typeof toolSchema>;

// Tool definition with value and label
export const toolDefinitionSchema = z.object({
  value: toolSchema,
  label: z.string(),
  description: z.string().optional(),
});

export type ToolDefinition = z.infer<typeof toolDefinitionSchema>;

// Response type for GET /tools endpoint
export type ToolsResponse = ToolDefinition[];

// Tool labels mapping (single source of truth)
export const TOOL_LABELS: Record<Tool, string> = {
  capture_availability: 'Capture Availability',
  capture_consent: 'Capture Consent',
  capture_lead: 'Capture Lead',
  search_car: 'Search Car Stock',
  request_human_callback: 'Request Human Callback',
  transfer_to_human: 'Transfer to Human',
  send_finance_link: 'Send Finance Link',
  capture_finance_application: 'Capture Finance Application',
  end_call: 'End Call',
  hold_for_transfer: 'Hold for Transfer',
  google_maps_search: 'Google Maps Search',
  web_search: 'Web Search',
  show_progress: 'Show Progress',
};

export const TOOL_DESCRIPTIONS: Record<Tool, string> = {
  capture_availability: 'Captures availability slots from customers',
  capture_consent: 'Checks if customer agrees to be called by AI assistant in the future',
  capture_lead: 'Saves customer vehicle interest and contact details',
  search_car: 'Searches the car stock based on customer preferences',
  request_human_callback: 'Escalates to human when customer goes off-topic or needs assistance',
  transfer_to_human: 'Transfers the active call to a human representative in real-time',
  send_finance_link: 'Texts the caller the finance-application link for a specific car',
  capture_finance_application:
    "Collects the caller's finance pre-qualification details on the call and saves them",
  end_call: 'Ends the call gracefully when the conversation is complete',
  hold_for_transfer:
    'Keeps the agent silent while the call is put through to someone else, until a new person speaks',
  google_maps_search:
    'Looks up locations, distances, and directions using Google Maps when the caller asks about places',
  web_search:
    'Looks up public, factual information from the web (general knowledge, news, definitions) for off-topic questions the caller asks. Not for private, account, pay, contract, or booking queries.',
  show_progress:
    'Text channels only: lets the agent show the customer a short step ("Searching cars near you") while it works on a long task',
};
